import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { SEO } from "@/components/common/SEO";
import SmartImage from "@/components/common/SmartImage";
import MatchRing from "@/components/common/MatchRing";

const BODIES = ["SUV", "Sedan", "Hatchback", "MPV"];
const FUELS = ["Any", "Petrol", "Diesel", "EV", "Hybrid"];
const USES = ["City", "Highway", "Family", "Off-road"];
const MUST = ["sunroof", "ADAS", "automatic", "6+ seats", "4WD"];

const STEPS = [
  { key: "budget", label: "What's your budget?" },
  { key: "body", label: "Body type?" },
  { key: "fuel", label: "Fuel preference?" },
  { key: "use", label: "Primary use?" },
  { key: "extras", label: "Must-haves?" },
];

export default function Advisor() {
  const [step, setStep] = useState(0);
  const [prefs, setPrefs] = useState({
    budget_min: 800000, budget_max: 2000000,
    body_types: [], fuel_preference: "Any", primary_use: "City",
    must_haves: [], seats_needed: 5, daily_km: 30, parking_charging: "unsure",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const toggleArr = (key, value) =>
    setPrefs((p) => ({
      ...p, [key]: p[key].includes(value) ? p[key].filter((v) => v !== value) : [...p[key], value],
    }));

  const submit = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.post("/ai/advisor", prefs);
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "AI advisor failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="advisor-page">
      <SEO title="AI Car Advisor" description="Tell us how you drive — get a personalised 3-car shortlist with match scores."/>
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">AI Advisor</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Pick the perfect car for <em className="not-italic text-ai">you</em>.</h1>
      <p className="text-muted-foreground mt-3 max-w-xl">Answer a few quick questions. Our AI matches them to India's catalogue and explains every recommendation.</p>

      {!result && (
        <div className="mt-10 rounded-3xl border border-border bg-card/30 p-8 sm:p-12">
          <div className="flex items-center gap-2 mb-6">
            {STEPS.map((s, i) => (
              <div key={s.key} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-gradient-ai" : "bg-secondary"}`}/>
            ))}
          </div>
          <div data-testid={`advisor-step-${step}`}>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Step {step + 1} of {STEPS.length}</div>
            <h2 className="font-display text-2xl sm:text-3xl mt-2 mb-8">{STEPS[step].label}</h2>

            {step === 0 && (
              <div>
                <div className="flex flex-wrap gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Min</span>
                    <input type="number" data-testid="advisor-budget-min" value={prefs.budget_min} onChange={(e)=>setPrefs({...prefs, budget_min: +e.target.value})} className="h-10 w-32 rounded-xl bg-secondary border border-border px-3"/>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Max</span>
                    <input type="number" data-testid="advisor-budget-max" value={prefs.budget_max} onChange={(e)=>setPrefs({...prefs, budget_max: +e.target.value})} className="h-10 w-32 rounded-xl bg-secondary border border-border px-3"/>
                  </div>
                  <div className="text-sm text-muted-foreground py-2">{formatINR(prefs.budget_min)} – {formatINR(prefs.budget_max)}</div>
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="flex flex-wrap gap-2">
                {BODIES.map((b) => (
                  <button key={b} data-testid={`advisor-body-${b}`} onClick={() => toggleArr("body_types", b)} className={`h-10 px-4 rounded-full border text-sm ${prefs.body_types.includes(b) ? "bg-[#2563EB] text-white border-[#2563EB]" : "border-border hover:bg-secondary"}`}>{b}</button>
                ))}
                <p className="basis-full text-xs text-muted-foreground mt-2">Pick one or more, or skip.</p>
              </div>
            )}
            {step === 2 && (
              <div className="flex flex-wrap gap-2">
                {FUELS.map((f) => (
                  <button key={f} data-testid={`advisor-fuel-${f}`} onClick={() => setPrefs({...prefs, fuel_preference: f})} className={`h-10 px-4 rounded-full border text-sm ${prefs.fuel_preference === f ? "bg-[#2563EB] text-white border-[#2563EB]" : "border-border hover:bg-secondary"}`}>{f}</button>
                ))}
              </div>
            )}
            {step === 3 && (
              <div className="flex flex-wrap gap-2">
                {USES.map((u) => (
                  <button key={u} data-testid={`advisor-use-${u}`} onClick={() => setPrefs({...prefs, primary_use: u})} className={`h-10 px-4 rounded-full border text-sm ${prefs.primary_use === u ? "bg-[#2563EB] text-white border-[#2563EB]" : "border-border hover:bg-secondary"}`}>{u}</button>
                ))}
                <div className="basis-full mt-4 flex items-center gap-3 text-sm">
                  <span className="text-muted-foreground">Daily kms</span>
                  <input type="number" data-testid="advisor-daily-km" value={prefs.daily_km} onChange={(e)=>setPrefs({...prefs, daily_km: +e.target.value})} className="h-10 w-24 rounded-xl bg-secondary border border-border px-3"/>
                </div>
              </div>
            )}
            {step === 4 && (
              <div className="flex flex-wrap gap-2">
                {MUST.map((m) => (
                  <button key={m} data-testid={`advisor-must-${m}`} onClick={() => toggleArr("must_haves", m)} className={`h-10 px-4 rounded-full border text-sm ${prefs.must_haves.includes(m) ? "bg-[#2563EB] text-white border-[#2563EB]" : "border-border hover:bg-secondary"}`}>{m}</button>
                ))}
              </div>
            )}

            <div className="mt-10 flex items-center justify-between">
              <button data-testid="advisor-prev" onClick={prev} disabled={step === 0} className="h-10 px-4 rounded-full border border-border text-sm disabled:opacity-40">Back</button>
              {step < STEPS.length - 1 ? (
                <button data-testid="advisor-next" onClick={next} className="h-11 px-6 rounded-full bg-[#2563EB] text-white hover:bg-[#1D4ED8] text-sm font-medium inline-flex items-center gap-2 hover:opacity-90">Next <ArrowRight className="w-4 h-4"/></button>
              ) : (
                <button data-testid="advisor-submit" onClick={submit} disabled={loading} className="h-11 px-6 rounded-full bg-gradient-ai text-white text-sm font-semibold inline-flex items-center gap-2 hover:opacity-95">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Thinking…</> : <><Sparkles className="w-4 h-4"/> Find my cars</>}
                </button>
              )}
            </div>
            {error && <div className="mt-4 text-sm text-destructive" data-testid="advisor-error">{error}</div>}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-10" data-testid="advisor-result">
          <div className="rounded-3xl border border-border bg-card/30 p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-ai font-semibold">AI Summary</div>
            <div className="font-display text-2xl mt-2 tracking-tight">{result.summary}</div>
          </div>
          <div className="mt-6 grid md:grid-cols-3 gap-5">
            {result.recommendations.map((r, i) => (
              <div key={r.car_slug} data-testid={`advisor-rec-${i}`} className="rounded-3xl border border-border bg-card/30 overflow-hidden flex flex-col">
                <div className="aspect-[16/10] overflow-hidden"><SmartImage src={r.image} alt={r.model} label={r.model}/></div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{r.brand_name}</div>
                      <div className="font-display text-2xl tracking-tight">{r.model}</div>
                      <div className="font-mono text-sm mt-1">{formatINR(r.price_min)} – {formatINR(r.price_max)}</div>
                    </div>
                    <MatchRing score={r.match_score}/>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{r.reasoning}</p>
                  <ul className="mt-4 space-y-1 text-sm">
                    {r.highlights?.map((h, idx) => <li key={idx} className="flex gap-2"><span className="text-ai">✦</span><span>{h}</span></li>)}
                  </ul>
                  <Link to={`/cars/${r.car_slug}`} className="mt-5 text-sm inline-flex items-center gap-1 text-foreground/80 hover:text-foreground">View details <ArrowRight className="w-3 h-3"/></Link>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex gap-3 flex-wrap">
            <Link to={`/compare?slugs=${result.recommendations.map(r=>r.car_slug).join(',')}`} data-testid="advisor-compare-all" className="h-11 px-5 rounded-full bg-[#2563EB] text-white hover:bg-[#1D4ED8] text-sm font-medium inline-flex items-center gap-2 hover:opacity-90">Deep compare these <ArrowRight className="w-4 h-4"/></Link>
            <button onClick={() => { setResult(null); setStep(0); }} data-testid="advisor-restart" className="h-11 px-5 rounded-full border border-border text-sm">Start over</button>
          </div>
        </div>
      )}
    </div>
  );
}
